import type { v4, Manifest } from '@sap/ux-specification-types';
import { descriptionSrcURL, syncRule, validity } from '../../../common/decoration/decorators';
import { getColumnPositionPathForView, getColumnsPathForLR, getColumnsPathForView } from '../../application';
import type { Importance } from 'sap/uxap/library';

export class ActionAfterExecutionConfiguration implements v4.ActionAfterExecutionConfiguration {
    @syncRule({
        manifest: {
            path: (
                pageName: string[],
                manifest: Manifest,
                sectionId: string,
                columnKey: string,
                targetAnnotation: string
            ): string =>
                getColumnsPathForLR(pageName, manifest, sectionId, columnKey, targetAnnotation) + '/afterExecution'
        }
    })
    @descriptionSrcURL('https://ui5.sap.com/#/topic/2c65f07f44094012a511d6bd83f50f2d')
    navigateToInstance?: boolean;

    @syncRule({
        manifest: {
            path: (
                pageName: string[],
                manifest: Manifest,
                sectionId: string,
                columnKey: string,
                targetAnnotation: string
            ): string =>
                getColumnsPathForLR(pageName, manifest, sectionId, columnKey, targetAnnotation) + '/afterExecution'
        }
    })
    enableAutoScroll?: boolean;
}

/************************************
 * Sync class for TableColumn
 */

export class TableColumn implements v4.TableColumn {
    @syncRule({
        manifest: {
            path: getColumnsPathForLR
        }
    })
    @validity({
        since: '1.84.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/api/sap.ui.core.CSSSize')
    width?: string;

    @syncRule({
        manifest: {
            path: getColumnsPathForLR
        }
    })
    @validity({
        since: '1.85.0'
    })
    availability?: v4.Availability;

    @syncRule({
        manifest: {
            path: getColumnsPathForLR
        }
    })
    @validity({
        since: '1.120.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/#/topic/c0f6592a592e47f9bb6d09900de47412')
    widthIncludingColumnHeader?: boolean;
}

/************************************
 * Sync class for ViewTableColumn
 */

export class ViewTableColumn implements v4.ViewTableColumn {
    @syncRule({
        manifest: {
            path: getColumnsPathForView
        }
    })
    @validity({
        since: '1.84.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/api/sap.ui.core.CSSSize')
    width?: string;

    @syncRule({
        manifest: {
            path: getColumnsPathForView
        }
    })
    @validity({
        since: '1.85.0'
    })
    availability?: v4.Availability;

    @syncRule({
        manifest: {
            path: getColumnsPathForLR
        }
    })
    @validity({
        since: '1.120.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/#/topic/c0f6592a592e47f9bb6d09900de47412')
    widthIncludingColumnHeader?: boolean;
}

export class TableColumnAction extends TableColumn implements v4.TableColumnAction {
    @syncRule({
        manifest: {
            path: getColumnsPathForLR
        }
    })
    @validity({
        since: '1.84.0'
    })
    afterExecution?: ActionAfterExecutionConfiguration;

    @syncRule({
        manifest: {
            path: getColumnsPathForLR
        }
    })
    @validity({
        since: '1.142.0'
    })
    overflowGroup?: number;
}

export class ViewTableColumnAction extends ViewTableColumn implements v4.ViewTableColumnAction {
    @syncRule({
        manifest: {
            path: getColumnsPathForView
        }
    })
    @validity({
        since: '1.84.0'
    })
    afterExecution?: ActionAfterExecutionConfiguration;

    @syncRule({
        manifest: {
            path: getColumnsPathForView
        }
    })
    @validity({
        since: '1.142.0'
    })
    overflowGroup?: number;
}

/**
 * Sync class for Position
 */
export class Position implements v4.Position {
    @syncRule({
        manifest: {
            path: (
                pageName: string[],
                manifest: Manifest,
                sectionId: string,
                columnKey: string,
                targetAnnotation: string
            ): string => getColumnsPathForLR(pageName, manifest, sectionId, columnKey, targetAnnotation) + '/position'
        }
    })
    @validity({
        since: '1.84.0'
    })
    anchor?: string;
    @syncRule({
        manifest: {
            path: (
                pageName: string[],
                manifest: Manifest,
                sectionId: string,
                columnKey: string,
                targetAnnotation: string
            ): string => getColumnsPathForLR(pageName, manifest, sectionId, columnKey, targetAnnotation) + '/position'
        }
    })
    @validity({
        since: '1.84.0'
    })
    placement: v4.Placement;
}

/**
 * Sync class for ViewPosition
 */
export class ViewPosition implements v4.ViewPosition {
    @syncRule({
        manifest: {
            path: getColumnPositionPathForView
        }
    })
    @validity({
        since: '1.84.0'
    })
    anchor?: string;
    @syncRule({
        manifest: {
            path: getColumnPositionPathForView
        }
    })
    @validity({
        since: '1.84.0'
    })
    placement: v4.Placement;
}

/**
 * Sync class for TableCustomColumn
 */
export class TableCustomColumn implements v4.TableCustomColumn {
    @syncRule({
        manifest: {
            path: getColumnsPathForLR
        }
    })
    @validity({
        since: '1.84.0'
    })
    position?: Position;

    @syncRule({
        manifest: {
            path: getColumnsPathForLR
        }
    })
    @validity({
        since: '1.84.0'
    })
    header: string;

    @syncRule({
        manifest: {
            path: getColumnsPathForLR
        }
    })
    @validity({
        since: '1.84.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/api/sap.ui.core.CSSSize')
    width?: string;

    @syncRule({
        manifest: {
            path: getColumnsPathForLR
        }
    })
    @validity({
        since: '1.84.0'
    })
    template: string;

    @syncRule({
        manifest: {
            path: getColumnsPathForLR
        }
    })
    @validity({
        since: '1.86.0'
    })
    horizontalAlign?: v4.HorizontalAlign;

    @syncRule({
        manifest: {
            path: getColumnsPathForLR
        }
    })
    @validity({
        since: '1.84.0'
    })
    availability?: v4.Availability;

    @syncRule({
        manifest: {
            path: getColumnsPathForLR
        }
    })
    @validity({
        since: '1.85.0'
    })
    properties?: v4.ColumnPropertiesType;

    @syncRule({
        manifest: {
            path: getColumnsPathForLR
        }
    })
    @validity({
        since: '1.120.0'
    })
    required?: boolean;

    @syncRule({
        manifest: {
            path: getColumnsPathForLR
        }
    })
    @validity({
        since: '1.120.0'
    })
    importance?: Importance;

    @syncRule({
        manifest: {
            path: getColumnsPathForLR
        }
    })
    @validity({
        since: '1.130.0'
    })
    tooltip?: string;
}

/**
 * Sync class for ViewTableCustomColumn
 */
export class ViewTableCustomColumn implements v4.ViewTableCustomColumn {
    @syncRule({
        manifest: {
            path: getColumnsPathForView
        }
    })
    @validity({
        since: '1.84.0'
    })
    position?: Position;
    @syncRule({
        manifest: {
            path: getColumnsPathForView
        }
    })
    @validity({
        since: '1.84.0'
    })
    header: string;
    @syncRule({
        manifest: {
            path: getColumnsPathForView
        }
    })
    @validity({
        since: '1.84.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/api/sap.ui.core.CSSSize')
    width?: string;
    @syncRule({
        manifest: {
            path: getColumnsPathForView
        }
    })
    @validity({
        since: '1.84.0'
    })
    template: string;
    @syncRule({
        manifest: {
            path: getColumnsPathForView
        }
    })
    @validity({
        since: '1.86.0'
    })
    horizontalAlign?: v4.HorizontalAlign;
    @syncRule({
        manifest: {
            path: getColumnsPathForView
        }
    })
    @validity({
        since: '1.84.0'
    })
    availability?: v4.Availability;
    @syncRule({
        manifest: {
            path: getColumnsPathForView
        }
    })
    @validity({
        since: '1.85.0'
    })
    properties?: v4.ColumnPropertiesType;

    @syncRule({
        manifest: {
            path: getColumnsPathForView
        }
    })
    @validity({
        since: '1.120.0'
    })
    required?: boolean;

    @syncRule({
        manifest: {
            path: getColumnsPathForView
        }
    })
    @validity({
        since: '1.120.0'
    })
    importance?: Importance;

    @syncRule({
        manifest: {
            path: getColumnsPathForView
        }
    })
    @validity({
        since: '1.130.0'
    })
    tooltip?: string;
}
